import React, { ReactNode, forwardRef } from 'react';
import { ANALYTICS_EVENT, ANALYTICS_PROPERTY } from '@kouto/types';
import { analyticsManager } from 'features/analytics';

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children?: ReactNode;
  analyticEvent?: ANALYTICS_EVENT;
  analyticData?: Partial<Record<ANALYTICS_PROPERTY, string>>;
  isPrimary?: boolean;
  big?: boolean;
  selected?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClick?: React.MouseEventHandler<any>; // setting this to any for backwards compatibility
}

const BaseButton = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ children, onClick, analyticEvent, analyticData, ...props }, ref) => (
    <button
      // eslint-disable-next-line react/button-has-type
      type={props.type || 'button'}
      ref={ref}
      {...props}
      onClick={(e) => {
        if (analyticEvent) {
          analyticsManager.sendEvent(analyticEvent, analyticData);
        }
        onClick?.(e);
      }}
    >
      {children}
    </button>
  ),
);

export interface ButtonLinkProps
  extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  children?: ReactNode;
  analyticEvent?: ANALYTICS_EVENT;
  analyticData?: Partial<Record<ANALYTICS_PROPERTY, string>>;
  isPrimary?: boolean;
  big?: boolean;
  selected?: boolean;
  disabled?: boolean;
}

const BaseButtonLink = forwardRef<HTMLAnchorElement, ButtonLinkProps>(
  (
    { children, onClick, analyticEvent, analyticData, disabled, ...props },
    ref,
  ) => (
    <a
      ref={ref}
      {...props}
      role="link"
      tabIndex={0}
      aria-disabled={disabled ? true : undefined}
      className={
        disabled ? `${props.className || ''} disabled`.trim() : props.className
      }
      onClick={(e) => {
        if (disabled) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        if (analyticEvent) {
          analyticsManager.sendEvent(analyticEvent, analyticData);
        }
        onClick?.(e);
      }}
      onKeyDown={(e) => {
        if (disabled) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (analyticEvent) {
            analyticsManager.sendEvent(analyticEvent, analyticData);
          }
          const mouseEvent = new MouseEvent('click', {
            bubbles: true,
            cancelable: true,
            view: window,
          });
          onClick?.(
            mouseEvent as unknown as React.MouseEvent<HTMLAnchorElement>,
          );
        }
      }}
    >
      {children}
    </a>
  ),
);

export { BaseButtonLink };
export default BaseButton;
